<!--
	Renders one icon from a MapLibre sprite sheet as a CSS-background crop, so the
	exact map symbol shows without a map instance. An optional `halo` draws a tinted
	SDF outline behind the icon, using the outline sheet as a CSS mask.
-->
<script lang="ts" module>
	interface SpriteRect {
		x: number;
		y: number;
		width: number;
		height: number;
	}

	// Sprite metadata fetched once per sheet path, shared by every icon instance.
	const sheetCache: Record<string, Promise<Record<string, SpriteRect>>> = {};

	function getSpriteMeta(path: string) {
		// A failed fetch renders nothing and is evicted so the next instance retries.
		return (sheetCache[path] ??= fetch(`${path}.json`)
			.then((r) => (r.ok ? r.json() : {}))
			.catch(() => {
				delete sheetCache[path];
				return {};
			}));
	}
</script>

<script lang="ts">
	interface Props {
		// Map image id, e.g. `sprite:icon`; sprite JSON keys are unprefixed (`addSprite` adds the namespace).
		icon: string;
		// Sheet path without the `.json`/`.png` suffix.
		spritePath: string;
		// Target height in px; the crop scales to preserve aspect ratio. Defaults to native size.
		size?: number;
		// Sprite key to fall back to when `icon` is absent from the sheet.
		fallback?: string;
		// Tinted outline drawn behind the icon at the same scale.
		halo?: { icon: string; color: string; spritePath: string };
	}

	const { icon, spritePath, size, fallback, halo }: Props = $props();

	const bare = (id: string) => id.split(':').pop() ?? id;
</script>

{#snippet crop(
	path: string,
	rect: SpriteRect,
	scale: number,
	left: number,
	top: number,
	color?: string
)}
	<div
		class="layer"
		style:left="{left}px"
		style:top="{top}px"
		style:width="{rect.width * scale}px"
		style:height="{rect.height * scale}px">
		<div
			class="icon"
			class:tint={!!color}
			style:width="{rect.width}px"
			style:height="{rect.height}px"
			style:transform="scale({scale})"
			style:background-color={color}
			style:--sheet="url({path}.png)"
			style:--pos="-{rect.x}px -{rect.y}px">
		</div>
	</div>
{/snippet}

{#await Promise.all( [getSpriteMeta(spritePath), halo ? getSpriteMeta(halo.spritePath) : undefined] ) then [sprite, outline]}
	{@const rect = sprite[bare(icon)] ?? (fallback ? sprite[fallback] : undefined)}
	{#if rect}
		{@const height = size ?? rect.height}
		{@const scale = height / rect.height}
		{@const haloRect = halo ? outline?.[bare(halo.icon)] : undefined}
		{@const boxW = (haloRect ?? rect).width * scale}
		{@const boxH = haloRect ? haloRect.height * scale : height}
		<div class="stack" style:width="{boxW}px" style:height="{boxH}px">
			{#if haloRect && halo}
				{@render crop(
					halo.spritePath,
					haloRect,
					scale,
					(boxW - haloRect.width * scale) / 2,
					(boxH - haloRect.height * scale) / 2,
					halo.color
				)}
			{/if}
			{@render crop(spritePath, rect, scale, (boxW - rect.width * scale) / 2, (boxH - height) / 2)}
		</div>
	{:else}
		<div class="missing"></div>
	{/if}
{/await}

<style>
	.stack {
		position: relative;
		flex-shrink: 0;
	}

	.layer {
		position: absolute;
		overflow: hidden;
	}

	.icon {
		transform-origin: 0 0;

		&:not(.tint) {
			background-image: var(--sheet);
			background-position: var(--pos);
			background-repeat: no-repeat;
		}

		&.tint {
			mask: var(--sheet) var(--pos) no-repeat;
			-webkit-mask: var(--sheet) var(--pos) no-repeat;
		}
	}

	.missing {
		flex-shrink: 0;
		border: 1px dashed var(--border-color);
		width: var(--icon-md);
		height: var(--icon-md);
	}
</style>
